import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { useTheme } from '@/contexts/ThemeContext';
import { Timestamp } from 'firebase/firestore';
import { db } from '@/js/external-js/config/main_config';
import { getMenuSettings } from '@/services/menusService';
import { RiskRegister } from '@/js/external-js/variableModules/namespaces/RiskRegister.namespace';

// Add window property types for TS
declare global {
  interface Window {
    assignees?: any[];
    currentUser?: { userID: string;[key: string]: any };
  }
}

interface ReviewRiskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: {
    title?: string;
    description?: string;
    date?: string;
    id?: string;
    prevReviewDate?: string;
    reviewDate?: string;
    nextReviewDate?: string;
  };
  onDataChanged?: () => void;
}

// Default risk responses if menus service is not available
const defaultRiskResponses = [
  { label: 'Treat', value: 'treat' },
  { label: 'Tolerate', value: 'tolerate' },
  { label: 'Transfer', value: 'transfer' },
  { label: 'Terminate', value: 'terminate' },
];

const ReviewRiskDialog: React.FC<ReviewRiskDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged }) => {
  const [riskResponses, setRiskResponses] = useState<{ label: string, value: string }[]>(defaultRiskResponses);

  // Load risk responses from menus service
  useEffect(() => {
    const loadRiskResponses = async () => {
      try {
        const menuSettings = await RiskRegister.Risk.Menu(window.companyID);
        console.log('[ReviewRiskDialog] Got menuSettings:', menuSettings);
        const responses = menuSettings.response;
        if (responses.length > 0) {
          setRiskResponses(
            responses.map((option: string) => ({
              label: option.charAt(0).toUpperCase() + option.slice(1).toLowerCase(),
              value: option.toLowerCase()
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load risk responses:', error);
        // Keep default responses if loading fails
      }
    };

    if (open) {
      loadRiskResponses();
    }
  }, [open]);

  // Reset all dialog state when dialog opens or selectedEvent changes
  useEffect(() => {
    if (!open) return;
    setReviewedBy('');
    setRiskResponse('accept');
    setTreatmentPlan('');
    setComments('');
    setReviewDate(new Date());
    setNextReviewDate(new Date(new Date().setMonth(new Date().getMonth() + 12)));
    setUsers([]);
    // Add any other state resets as needed
  }, [open, selectedEvent]);
  const { isDark } = useTheme();
  const [reviewedBy, setReviewedBy] = useState('');
  const [riskResponse, setRiskResponse] = useState('accept');
  const [treatmentPlan, setTreatmentPlan] = useState('');
  const [comments, setComments] = useState('');
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]);
  const [reviewDate, setReviewDate] = useState<Date | undefined>(
    new Date()
  );
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(
    new Date(new Date().setMonth(new Date().getMonth() + 12))
  );

  useEffect(() => {
    async function fetchUsers() {
      // Match PolicyAdmin: allow for async, robust fallback, and label/value fields
      let usersArr: any[] = [];
      if (window.assignees && Array.isArray(window.assignees)) {
        usersArr = window.assignees;
      }
      // fallback: try fetch from API if needed (future-proofing, not active here)
      const formatted = usersArr.map((user: any) => ({
        label: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || user.userID,
        value: user.userID,
      }));
      setUsers(formatted);
      // Default to current user if available, else first user
      if (window.currentUser && window.currentUser.userID) {
        setReviewedBy(window.currentUser.userID);
      } else if (usersArr.length > 0) {
        setReviewedBy(usersArr[0].userID);
      }
    }
    if (open) fetchUsers();
  }, [open]);

  const handleCompleteReview = async () => {
    try {
      // Start loading state
      console.log('[RiskReview] Saving risk review...');



      const riskId = selectedEvent?.id;
      if (!riskId) throw new Error('No risk ID provided.');
      let riskData = await RiskRegister.RiskProxy.Get(riskId);
      riskData = riskData.data;


      if (!riskData) throw new Error('No risk data found for given ID.');

      // Fetch existing Firestore risk document
      const riskDocRef = db.collection('customers').doc(window.companyID).collection('compliance').doc('risk');
      const riskDoc = await riskDocRef.get();

      let versionSubNumber = 0;
      let versionNumber = 1;
      let versionKeys = [];

      if (riskDoc.exists && riskDoc.data()?.version) {
        versionKeys = Object.keys(riskDoc.data()?.version);
        const lastVersionKey = versionKeys[versionKeys.length - 1];
        const versionInfo = riskDoc.data()?.version?.[lastVersionKey];
        versionSubNumber = parseInt(versionInfo?.versionNumber?.split('.')[1] || '0');
        versionNumber = parseInt(versionInfo?.versionNumber?.split('.')[0] || '1');
      }

      // Build new version string
      const newVersion = autoIncrementVersion(riskData.version || '1.0') || '1.0';
      const versionLabel = isNaN(versionSubNumber)
        ? `${versionNumber}.0`
        : versionSubNumber < 9
          ? `${versionNumber}.${versionSubNumber + 1}`
          : `${versionNumber + 1}.0`;

      // Historical log of what changed
      const riskHistory = Array.isArray(riskData?.history) ? [...riskData.history] : [];
      riskHistory.push({
        dateChanged: Timestamp.now(),
        changedBy: window.currentUser?.userID || '',
        version: newVersion,
        riskNew: {
          lastReviewDate: reviewDate || '',
          nextReviewDate: nextReviewDate || '',
          treatmentPlan: riskResponse === 'treat' ? treatmentPlan : '',
          response: riskResponse,
          version: newVersion
        }
      });

      // Build new risk object
      const updatedRisk = {
        ...riskData,
        lastReviewDate: reviewDate || '',
        nextReviewDate: nextReviewDate || '',
        treatmentPlan: riskResponse === 'treat' ? treatmentPlan : '',
        response: riskResponse,
        version: newVersion,
      };

      // Save updated risk
      await RiskRegister.RiskProxy.Edit(riskId, updatedRisk);

      // Create snapshot for global riskHistory array
      // Strip riskHistory and history from updatedRisk
      const { riskHistory: _, history: __, ...rest } = updatedRisk;

      // Locate the correct document in the 'register' collection to get its Firestore document ID
      let entryDoc;
      const riskRegisterRef = db
        .collection('customers')
        .doc(window.companyID)
        .collection('compliance')
        .doc('risk');

      if (isNaN(parseInt(riskId))) {
        // If riskId is not a number, assume it's a componentId
        const componentIdQuery = riskRegisterRef.collection("register").where("componentId", "==", riskId);
        entryDoc = await componentIdQuery.limit(1).get();
      } else {
        // Try by numeric riskID
        const riskIdQuery = riskRegisterRef.collection("register").where("riskID", "==", parseInt(riskId));
        entryDoc = await riskIdQuery.limit(1).get();

        // Fallback to componentId if no match
        if (entryDoc.empty) {
          const componentIdQuery = riskRegisterRef.collection("register").where("componentId", "==", riskId);
          entryDoc = await componentIdQuery.limit(1).get();
        }
      }

      if (entryDoc.empty) {
        throw new Error(`No register entry found for ID: ${riskId}`);
      }

      const documentId = entryDoc.docs[0].id; // Firestore document ID

      // Push snapshot to riskHistory collection (as a new doc)
      const docRef = await db
        .collection('customers')
        .doc(window.companyID)
        .collection('compliance')
        .doc('risk')
        .collection('riskHistory')
        .add({
          changedBy: window.currentUser?.userID || '',
          dateChanged: Timestamp.now(),
          riskNew: rest,
          version: newVersion,
          comments: comments,
          riskID: riskId,
          docID: documentId
        });

      console.log(`[RiskReview] ✅ Risk history entry added: ${docRef.id}`);
      console.log('[RiskReview] ✅ Risk updated successfully:', updatedRisk);
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err) {
      console.error('[RiskReview] ❌ Failed to save risk review:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Risk Review</DialogTitle>
            {/* <DialogDescription className="text-muted-foreground">
            {selectedEvent?.title || 'Risk Review Task'}
          </DialogDescription> */}
          </div>
          <div className="grid gap-4 pb-4 px-4">
            {/* Risk Response (above name) */}
            {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="risk-response" className="text-right text-foreground">
              Risk Response
            </Label>
            <div className="col-span-3">
              <Select value={riskResponse} onValueChange={setRiskResponse}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select response" />
                </SelectTrigger>
                <SelectContent>
                  {riskResponses.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div> */}
            {/* Name (expandable textarea) */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="risk-name" className="text-right text-foreground">
                Description
              </Label>
              <Textarea id="risk-name" value={selectedEvent?.title.replace('Review Risk: ', '') || ''} readOnly className="col-span-3 border border-gray-200 rounded-md resize-y min-h-[40px] max-h-[200px]" />
            </div>
            {/* Reviewed By (user dropdown) */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reviewed-by" className="text-right text-foreground">
                Reviewed By
              </Label>
              <div className="col-span-3 w-full">
                <SingleUserCombobox
                  selected={reviewedBy}
                  setSelected={setReviewedBy}
                  placeholder="Select reviewer..."
                  className="w-full"
                />
              </div>
            </div>
            {/* Last Reviewed */}
            {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="risk-prev-review-date" className="text-right text-foreground">
              Last Reviewed
            </Label>
            <div className="col-span-3">
              <DatePicker date={selectedEvent?.prevReviewDate ? new Date(selectedEvent.prevReviewDate) : undefined} setDate={() => { }} readOnly fullWidth placeholder="No date" />
            </div>
          </div> */}
            {/* Date Reviewed */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="risk-review-date" className="text-right text-foreground">
                Date Reviewed
              </Label>
              <div className="col-span-3">
                <DatePicker date={reviewDate} setDate={setReviewDate} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
              </div>
            </div>
            {/* Next Review */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="risk-next-review-date" className="text-right text-foreground">
                Next Review Date
              </Label>
              <div className="col-span-3">
                <DatePicker date={nextReviewDate} setDate={setNextReviewDate} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
              </div>
            </div>
            {/* Treatment Plan (conditional, textarea under next review) */}
            {riskResponse === 'treat' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="treatment-plan" className="text-right text-foreground">
                  Treatment Plan
                </Label>
                <div className="col-span-3">
                  <Textarea id="treatment-plan" value={treatmentPlan} onChange={e => setTreatmentPlan(e.target.value)} placeholder="Describe the treatment plan..." className="border border-gray-200 rounded-md min-h-[40px] max-h-[200px] resize-y" />
                </div>
              </div>
            )}
            {/* Comments */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="comments" className="text-right text-foreground">
                Comments
              </Label>
              <div className="col-span-3">
                <Textarea id="comments" rows={4} onChange={e => setComments(e.target.value)} placeholder="Add notes about the risk review..." />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <div className="text-xs text-gray-400 mt-2 ml-1">Component ID: {selectedEvent?.id || ''}</div>
            <div className="flex flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border border-gray-200 hover:bg-gray-50 hover:text-black dark:hover:text-white">
                Cancel
              </Button>
              <Button onClick={handleCompleteReview}>Complete Review</Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewRiskDialog;

function autoIncrementVersion(version: string): string {
  const parts = version.split('.');
  let major = parseInt(parts[0]) || 1;
  let minor = parseInt(parts[1]) || 0;

  if (minor < 9) {
    minor += 1;
  } else {
    major += 1;
    minor = 0;
  }

  return `${major}.${minor}`;
}